// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The audit trail (design doc §12.3, US-F-01).
 *
 * Read-only, and it has no edit affordance anywhere by design: an audit log that can be
 * corrected is not evidence of anything.
 *
 * Each row names its entity the way the rest of the UI does — archive number, "type vX.Y",
 * type name — and its action in words; the raw codes stay one hover away, on the cell's
 * title. The request id, the join key to the server's structured logs, sits in the row's
 * details with the before/after summaries: it is what turns "the archive changed at 14:02"
 * into the request that changed it, but nobody reads it while scanning the list.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, exportUrls } from "@/api/endpoints";
import type { AuditLog } from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { useCursorList } from "@/hooks/useCursorList";

const ENTITY_TYPES = [
  "device_archive",
  "maintenance_event",
  "bom",
  "product_type",
  "robot_sn",
  "bom_import_batch",
  "archive_import_batch",
  "user_session",
] as const;

/** The page the entity lives on, when it has one that still exists. */
function entityHref(log: AuditLog): string | null {
  switch (log.entity_type) {
    case "device_archive":
      return `/archives/${log.entity_id}/current`;
    case "maintenance_event": {
      const archiveId = log.after_summary?.archive_id;
      return typeof archiveId === "string" ? `/archives/${archiveId}/maintenance` : null;
    }
    case "bom":
      return log.action === "bom.deleted" ? null : `/boms/${log.entity_id}`;
    default:
      return null;
  }
}

export function AuditLogs() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({ entity_type: "", entity_id: "", actor_email: "" });
  const [filters, setFilters] = useState(draft);

  const logs = useCursorList(["audit", filters], (cursor) =>
    api.audit.list({ ...filters, cursor, limit: 50 }),
  );

  return (
    <section>
      <h1>{t("audit.title")}</h1>

      <form
        className="filters"
        onSubmit={(event) => {
          event.preventDefault();
          setFilters(draft);
        }}
      >
        <select
          aria-label={t("audit.entity")}
          value={draft.entity_type}
          onChange={(event) => setDraft({ ...draft, entity_type: event.target.value })}
        >
          <option value="">{t("audit.allEntityTypes")}</option>
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`audit.entityTypes.${type}`)}
            </option>
          ))}
        </select>
        <input
          aria-label={t("audit.entityId")}
          placeholder={t("audit.entityId")}
          className="mono"
          value={draft.entity_id}
          onChange={(event) => setDraft({ ...draft, entity_id: event.target.value })}
        />
        <input
          aria-label={t("audit.actorEmail")}
          placeholder={t("audit.actorEmail")}
          value={draft.actor_email}
          onChange={(event) => setDraft({ ...draft, actor_email: event.target.value })}
        />
        <button type="submit">{t("common.search")}</button>
        <a className="link-button" href={exportUrls.audit(filters)}>
          {t("common.export")}
        </a>
      </form>

      <ErrorBanner error={logs.error} />

      <ResizableTable storageKey="audit-logs-2">
        <thead>
          <tr>
            <th>{t("audit.occurredAt")}</th>
            <th>{t("audit.actor")}</th>
            <th>{t("audit.action")}</th>
            <th>{t("audit.entity")}</th>
            <th>{t("audit.details")}</th>
          </tr>
        </thead>
        <tbody>
          {logs.items.map((log) => (
            <AuditRow key={log.id} log={log} />
          ))}
        </tbody>
      </ResizableTable>
      <Pager
        hasNextPage={logs.hasNextPage}
        isFetchingNextPage={logs.isFetchingNextPage}
        onMore={() => void logs.fetchNextPage()}
      />
    </section>
  );
}

function AuditRow({ log }: { log: AuditLog }) {
  const { t } = useTranslation();
  const href = entityHref(log);
  const label = log.entity_label ?? log.entity_id;
  return (
    <tr>
      <td className="cell-nowrap">{new Date(log.occurred_at).toLocaleString()}</td>
      <td>{log.actor_email ?? "—"}</td>
      <td title={log.action}>
        {t(`audit.actions.${log.action.replaceAll(".", "_")}`, { defaultValue: log.action })}
      </td>
      <td title={`${log.entity_type}/${log.entity_id}`}>
        <span className="audit-entity-type">
          {t(`audit.entityTypes.${log.entity_type}`, { defaultValue: log.entity_type })}
        </span>{" "}
        {href ? (
          <Link className={log.entity_label ? undefined : "mono"} to={href}>
            {label}
          </Link>
        ) : (
          <span className={log.entity_label ? undefined : "mono"}>{label}</span>
        )}
      </td>
      <td>
        <details className="audit-details">
          <summary>{t("audit.details")}</summary>
          <dl>
            <dt>{t("audit.requestId")}</dt>
            <dd className="mono">{log.request_id ?? "—"}</dd>
            <dt>{t("audit.entityId")}</dt>
            <dd className="mono">{log.entity_id}</dd>
            {log.before_summary && (
              <>
                <dt>{t("audit.before")}</dt>
                <dd>
                  <pre>{JSON.stringify(log.before_summary, null, 2)}</pre>
                </dd>
              </>
            )}
            {log.after_summary && (
              <>
                <dt>{t("audit.after")}</dt>
                <dd>
                  <pre>{JSON.stringify(log.after_summary, null, 2)}</pre>
                </dd>
              </>
            )}
          </dl>
        </details>
      </td>
    </tr>
  );
}
